import { supabase } from '../../lib/supabase';
import type { Band, Profile, Story, Event } from '../../types/database';
import { calculateDistance } from '../../utils/distance';

export type BandWithDetails = Band & {
  members: {
    profile: Profile;
    role: string;
    instruments: string[];
  }[];
  stories: (Story & {
    author: Profile;
    likes_count: number;
  })[];
  events: (Event & {
    creator: Profile;
  })[];
  distance?: number;
};

export async function fetchBandDetails(
  slug: string,
  coordinates?: { latitude: number; longitude: number } | null
): Promise<BandWithDetails> {
  try {
    const { data: band, error: bandError } = await supabase
      .from('bands')
      .select(`
        *,
        members:band_members(
          profile:profiles(*),
          role,
          instruments
        ),
        stories:stories(
          *,
          author:profiles!stories_author_id_fkey(*)
        )
      `)
      .eq('slug', slug)
      .single();

    if (bandError) throw bandError;
    if (!band) throw new Error('Band not found');

    const { data: eventBands, error: eventBandsError } = await supabase
      .from('event_bands')
      .select(`
        event:events(
          *,
          creator:profiles!events_created_by_fkey(*)
        )
      `)
      .eq('band_id', band.id);

    if (eventBandsError) throw eventBandsError;

    const events = eventBands?.map(eb => eb.event).filter(Boolean) || [];

    const storiesWithLikes = await Promise.all(
      (band.stories || []).map(async (story) => {
        const { count: likesCount } = await supabase
          .from('story_likes')
          .select('*', { count: 'exact' })
          .eq('story_id', story.id);

        return {
          ...story,
          likes_count: likesCount || 0,
        };
      })
    );

    let distance;
    if (coordinates && band.coordinates) {
      distance = calculateDistance(coordinates, band.coordinates);
    }

    return {
      ...band,
      stories: storiesWithLikes,
      events: events as (Event & { creator: Profile })[],
      distance,
    } as BandWithDetails;
  } catch (error) {
    console.error('Error fetching band details:', error);
    throw error;
  }
}

export async function updateBand(
  id: string,
  data: Partial<Band>
): Promise<Band> {
  try {
    const { data: updatedBand, error } = await supabase
      .from('bands')
      .update({
        ...data,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (!updatedBand) throw new Error('Failed to update band');

    return updatedBand;
  } catch (error) {
    console.error('Error updating band:', error);
    throw error;
  }
}

export async function deleteBand(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('bands')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting band:', error);
    throw error;
  }
}